import { FC, ReactNode } from 'react';

import { Box, css, Grid, styled, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import Image, { StaticImageData } from 'next/image';

import { Header, HeaderProps } from '@/components/header';
import { Title } from '@/components/title';
import {
  HEADER_HEIGHT,
  HEADER_MARGIN_BOTTOM,
  MAIN_PADDING_TOP_AND_BOTTOM
} from '@/constants/ui.constants';

interface Props extends HeaderProps {
  title: string;
  description: string;
  image: StaticImageData;
  children?: ReactNode;
}

// we are using this layout for all basic pages that including image, title and description - onboarding, contact, error and 404 pages.
export const BasicLayout: FC<Props> = ({
  title,
  description,
  image,
  logo = '',
  backToPath = null,
  onBackClick = () => null,
  hasLocalePicker = false,
  children
}) => {
  const { palette } = useTheme();

  return (
    <StyledContainer>
      <Header
        backToPath={backToPath}
        logo={logo}
        onBackClick={onBackClick}
        hasLocalePicker={hasLocalePicker}
      />

      <StyledWrapper container direction="column" flexWrap="nowrap" height="100%">
        <StyledGrid container direction="column" mb={10} flexShrink={1}>
          <Grid container direction="column" alignItems="center" height="100%" flexWrap="nowrap">
            <StyledImage src={image} alt="Placeholder image" priority />

            <Grid container direction="column" flexWrap="nowrap">
              <Title isCentered>{title}</Title>

              <Typography variant="body1" color={palette.text.primary} textAlign="center">
                {description}
              </Typography>
            </Grid>
          </Grid>
        </StyledGrid>

        <Box display="flex" flex={1} flexShrink={0} flexBasis="auto">
          {children}
        </Box>
      </StyledWrapper>
    </StyledContainer>
  );
};

const StyledContainer = styled('main')`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
`;

const StyledWrapper = styled(Grid)`
  max-height: calc(
    100vh - ${HEADER_HEIGHT + HEADER_MARGIN_BOTTOM}px - ${MAIN_PADDING_TOP_AND_BOTTOM}px
  );
  height: 100%;
  overflow-y: hidden;
`;

const StyledImage = styled(Image)(
  ({ theme }) => css`
    margin-bottom: ${theme.spacing(10)};
  `
);

const StyledGrid = styled(Grid)`
  overflow-y: auto;
`;
